
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';

import {
    BLANK_TYPE_OPTIONS,
    POLISHING_OPTIONS,
    formatMoney,
    toNumber,
} from './blankSupplierImport.utils';
import {
    estimateBlankPrice,
    getBlankSuppliers,
} from '../CatalogReview/catalogReviewApi';
import { useAdminRealtimeSemanticFields } from '../Presence/adminRealtimeSemantic';

const BLANK_ESTIMATOR_REALTIME_NAMESPACE = 'product-seo-blank-estimator-v1';

const normalizeDimensions = (dimensions = {}) => ({
    height: Math.max(0, toNumber(dimensions.height, 0)),
    width: Math.max(0, toNumber(dimensions.width, 0)),
    thickness: Math.max(0, toNumber(dimensions.thickness, 0)),
});

const BlankPriceEstimator = ({
    materialKey = '',
    materialName = '',
    initialBlankType = 'stele',
    initialPolishingType = 'two-side',
    initialSupplierKey = '',
    initialDimensions = null,
    suppliers: suppliedSuppliers = null,
    title = 'Оценить цену нестандартной заготовки',
    defaultOpen = false,
    compact = false,
    disabled = false,
    onUseEstimate = null,
}) => {
    const normalizedInitialDimensions = useMemo(
        () => normalizeDimensions(initialDimensions || {
            height: 1100,
            width: 900,
            thickness: 80,
        }),
        [
            initialDimensions?.height,
            initialDimensions?.width,
            initialDimensions?.thickness,
        ]
    );
    const [estimator, setEstimator] = useState({
        blankType: initialBlankType || 'stele',
        polishingType: initialPolishingType || 'two-side',
        supplierKey: initialSupplierKey || '',
        ...normalizedInitialDimensions,
    });
    const [loadedSuppliers, setLoadedSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [estimateResult, setEstimateResult] = useState(null);
    const [estimating, setEstimating] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(defaultOpen);

    const realtimeChannelKey = `estimator-${String(materialKey || 'none')}`;
    const {
        remoteState: sharedState,
        publishField: publishEstimatorField,
    } = useAdminRealtimeSemanticFields({
        namespace: BLANK_ESTIMATOR_REALTIME_NAMESPACE,
        channelKey: realtimeChannelKey,
    });

    const changeOpen = useCallback((nextOpen) => {
        const next = nextOpen === true;
        setOpen(next);
        publishEstimatorField('open', next);
    }, [publishEstimatorField]);

    const suppliers = Array.isArray(suppliedSuppliers)
        ? suppliedSuppliers
        : loadedSuppliers;

    useEffect(() => {
        if (!sharedState || typeof sharedState !== 'object') return;
        if (typeof sharedState.open === 'boolean') setOpen(sharedState.open);
        if (sharedState.estimator && typeof sharedState.estimator === 'object') {
            setEstimator((current) => ({
                ...current,
                ...sharedState.estimator,
            }));
        }
        if (Object.prototype.hasOwnProperty.call(sharedState, 'estimateResult')) {
            setEstimateResult(sharedState.estimateResult || null);
        }
    }, [sharedState]);

    useEffect(() => {
        setEstimator({
            blankType: initialBlankType || 'stele',
            polishingType: initialPolishingType || 'two-side',
            supplierKey: initialSupplierKey || '',
            ...normalizedInitialDimensions,
        });
        setEstimateResult(null);
        setError('');
    }, [
        materialKey,
        initialBlankType,
        initialPolishingType,
        initialSupplierKey,
        normalizedInitialDimensions.height,
        normalizedInitialDimensions.width,
        normalizedInitialDimensions.thickness,
    ]);

    useEffect(() => {
        if (Array.isArray(suppliedSuppliers)) return undefined;

        let cancelled = false;

        const loadSuppliers = async () => {
            try {
                setSuppliersLoading(true);
                const response = await getBlankSuppliers();

                if (!cancelled) {
                    setLoadedSuppliers(
                        Array.isArray(response?.suppliers)
                            ? response.suppliers
                            : []
                    );
                }
            } catch (requestError) {
                if (!cancelled) {
                    setError(
                        requestError?.response?.data?.message
                        || 'Не удалось загрузить поставщиков.'
                    );
                }
            } finally {
                if (!cancelled) setSuppliersLoading(false);
            }
        };

        loadSuppliers();

        return () => {
            cancelled = true;
        };
    }, [suppliedSuppliers]);

    const updateEstimator = (patch) => {
        setEstimator((current) => {
            const next = { ...current, ...patch };
            publishEstimatorField('estimator', next);
            return next;
        });
        setEstimateResult(null);
        publishEstimatorField('estimateResult', null);
        setError('');
    };

    const handleEstimate = async () => {
        if (!materialKey) {
            setError('Сначала выбери материал заготовки.');
            return;
        }

        if (
            !(toNumber(estimator.height) > 0)
            || !(toNumber(estimator.width) > 0)
            || !(toNumber(estimator.thickness) > 0)
        ) {
            setError('Укажи высоту, ширину и толщину заготовки.');
            return;
        }

        try {
            setEstimating(true);
            setEstimateResult(null);
            setError('');

            const response = await estimateBlankPrice({
                materialKey,
                blankType: estimator.blankType,
                polishingType: estimator.polishingType,
                supplierKey: estimator.supplierKey,
                height: toNumber(estimator.height, 0),
                width: toNumber(estimator.width, 0),
                thickness: toNumber(estimator.thickness, 0),
                allowEstimated: true,
            });

            const nextResult = response?.result || null;
            setEstimateResult(nextResult);
            publishEstimatorField('estimateResult', nextResult);
        } catch (requestError) {
            setError(
                requestError?.response?.data?.message
                || 'Не удалось оценить заготовку.'
            );
        } finally {
            setEstimating(false);
        }
    };

    const supplierName = (supplierKey) => (
        suppliers.find((supplier) => supplier.key === supplierKey)?.name
        || supplierKey
        || 'Рабочая цена'
    );

    return (
        <details
            className={[
                'blankPriceEstimator',
                'blankPriceEstimator--reusable',
                compact ? 'blankPriceEstimator--compact' : '',
            ].filter(Boolean).join(' ')}
            open={open}
        >
            <summary
                onClick={(event) => {
                    event.preventDefault();
                    changeOpen(!open);
                }}
            >
                {title}
            </summary>

            {materialName ? (
                <div className="blankPriceEstimator__material">
                    Материал: <strong>{materialName}</strong>
                </div>
            ) : null}

            <div className="blankPriceEstimator__fields">
                <label>
                    <span>Тип</span>
                    <select
                        value={estimator.blankType}
                        onChange={(event) => {
                            const blankType = event.target.value;
                            updateEstimator({
                                blankType,
                                polishingType: blankType === 'slab'
                                    ? 'one-side'
                                    : estimator.polishingType,
                                thickness: blankType === 'slab'
                                    ? 20
                                    : estimator.thickness,
                            });
                        }}
                        disabled={disabled || estimating}
                    >
                        {BLANK_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                <label>
                    <span>Полировка</span>
                    <select
                        value={estimator.polishingType}
                        onChange={(event) => updateEstimator({ polishingType: event.target.value })}
                        disabled={disabled || estimating}
                    >
                        {POLISHING_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                <label>
                    <span>Поставщик</span>
                    <select
                        value={estimator.supplierKey}
                        onChange={(event) => updateEstimator({ supplierKey: event.target.value })}
                        disabled={disabled || estimating || suppliersLoading}
                    >
                        <option value="">
                            {suppliersLoading ? 'Загрузка поставщиков...' : 'Самая выгодная оценка'}
                        </option>
                        {suppliers
                            .filter((supplier) => supplier.enabled !== false)
                            .map((supplier) => (
                                <option key={supplier.key} value={supplier.key}>{supplier.name}</option>
                            ))}
                    </select>
                </label>

                <label>
                    <span>Длина / высота</span>
                    <input
                        type="number"
                        min="1"
                        value={estimator.height}
                        onChange={(event) => updateEstimator({ height: event.target.value })}
                        disabled={disabled || estimating}
                    />
                </label>

                <label>
                    <span>Ширина</span>
                    <input
                        type="number"
                        min="1"
                        value={estimator.width}
                        onChange={(event) => updateEstimator({ width: event.target.value })}
                        disabled={disabled || estimating}
                    />
                </label>

                <label>
                    <span>Толщина</span>
                    <input
                        type="number"
                        min="1"
                        value={estimator.thickness}
                        onChange={(event) => updateEstimator({ thickness: event.target.value })}
                        disabled={disabled || estimating}
                    />
                </label>

                <button
                    type="button"
                    className="is-primary"
                    onClick={handleEstimate}
                    disabled={disabled || estimating || !materialKey}
                >
                    <CalculateOutlinedIcon fontSize="small" />
                    {estimating ? 'Считаю...' : 'Оценить'}
                </button>
            </div>

            {error ? (
                <div className="blankPriceEstimator__error">{error}</div>
            ) : null}

            {estimateResult ? (
                <div className={`blankPriceEstimator__result is-${estimateResult.confidence || 'none'}`}>
                    <div>
                        <span>{estimateResult.estimated ? 'Предполагаемая цена' : 'Точная цена'}</span>
                        <strong>{formatMoney(estimateResult.price)}</strong>
                        <small>{estimateResult.label}</small>
                    </div>
                    <div>
                        <span>Поставщик</span>
                        <strong>{supplierName(estimateResult.supplierKey)}</strong>
                        <small>Надёжность: {estimateResult.confidence || '—'}</small>
                    </div>
                    {estimateResult.estimatedRange ? (
                        <div>
                            <span>Диапазон</span>
                            <strong>
                                {formatMoney(estimateResult.estimatedRange.min)} — {formatMoney(estimateResult.estimatedRange.max)}
                            </strong>
                            <small>Не является подтверждённой ценой поставщика</small>
                        </div>
                    ) : null}
                    {estimateResult.references?.length ? (
                        <div className="is-wide">
                            <span>Основание</span>
                            {estimateResult.references.map((reference) => (
                                <small key={`${reference.label}-${reference.price}`}>
                                    {reference.label}: {formatMoney(reference.price)}
                                </small>
                            ))}
                        </div>
                    ) : null}

                    {typeof onUseEstimate === 'function' ? (
                        <button
                            type="button"
                            className="blankPriceEstimator__useButton"
                            onClick={() => onUseEstimate({
                                result: estimateResult,
                                estimator: {
                                    ...estimator,
                                    height: toNumber(estimator.height, 0),
                                    width: toNumber(estimator.width, 0),
                                    thickness: toNumber(estimator.thickness, 0),
                                },
                            })}
                            disabled={disabled || !(toNumber(estimateResult.price) > 0)}
                        >
                            Использовать эту оценку
                        </button>
                    ) : null}
                </div>
            ) : null}
        </details>
    );
};

export default BlankPriceEstimator;
